import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { flow } from 'lodash';
import PropTypes from 'prop-types';
import { getContext } from '@fiverr-private/fiverr_context';
import FilerrShape from '../../../Previewer/PropsShape/FilerrShape';
import Context, { contextPropType } from '../../context';
import './index.scss';
import * as FilesSelectors from '../../../Previewer/selectors/files';
import * as Selectors from '../../../Previewer/selectors';
import {
    deleteCommentByAnnotationId,
    overlayClicked, selectCommentIdByAnnotationId,
    updateCommentByAnnotation
} from '../../../Previewer/components/MediaOverlay/actios';
import * as CommentsSelectors from '../../../Previewer/selectors/comments';

import { collaborationEventBus, isTouchDevice, withContext } from '../../../Utils';
import { OVERLAY_CLICK_EVENT, PDF_TYPE } from '../../consts';
import { setCurrentCommentId } from '../../../Previewer/actions/current';
import * as CurrentsSelectors from '../../../Previewer/selectors/current';
import { selectAnnotationsHidden, selectCommentBoxCancelled } from '../../../Previewer/selectors/annotations';
import * as Error from '../../../Previewer/utils/error';
import * as SettingsSelectors from '../../../Previewer/selectors/settings';
import {
    addScriptTagSDK,
    createAdobeViewer,
    registerAnnotationEvents,
    registerViewerEvents,
    removeSaveButton,
    removeScriptTagSDK,
    setConfig
} from './pdf-embed-helper';
import { arrayDiff, arraySymetricDiff, getPPI } from './utils';
import { AnnotationManager } from './annotationManager';
import * as annotationEvents from './annotation-events';
import * as viewerEvents from './viewer-events';
import { ADOBE_PDF_EMBED_DIV_ID, CLIENT_ID } from './constants';
import { DEFAULT_ANNOTATION_COLOR } from './annotation-color-pallete';

class Pdf extends PureComponent {

    constructor(props) {
        super(props);

        this.state = {
            isTouch: isTouchDevice(),
            showToolbarHider: false,
            addedExistingAnnotations: true
        };
        this.iframeRef = React.createRef();
    }

    componentDidMount() {
        const { context } = this.props;
        addScriptTagSDK(() => this.createPdfViewer(), context.onError);
    }

    componentDidUpdate(prevProps) {
        const { commentsIds, currentCommentId, commentBoxCancelled, annotationsHidden, filerr, context } = this.props;
        if (this.annotationMNGR) {
            if (this.removedComments(prevProps)) {
                this.commentsRemoved(commentsIds, prevProps.comments);
            }
            if (currentCommentId !== prevProps.currentCommentId) {
                this.currentCommentChanged(currentCommentId);
            }
            if (this.changedTempAnnotations(prevProps.tempAnnotations)) {
                this.annotationMNGR.deleteAnnotations([prevProps.tempAnnotations[0].id]);
            }
            if (commentBoxCancelled && !prevProps.commentBoxCancelled) {
                this.deleteLastTempAnnotation(prevProps.tempAnnotations);
            }
            if (annotationsHidden !== prevProps.annotationsHidden) {
                this.toggleHideAnnotations(annotationsHidden);
            }
            if (this.pendingCommentsSubmitted(prevProps)) {
                this.updatePdfAnnotations(prevProps);
            }
            if (filerr && prevProps.filerr && filerr.id !== prevProps.filerr.id) {
                addScriptTagSDK(this.createPdfViewer(), context.onError);
            }
        }
    }

    componentWillUnmount() {
        this.clearViewerResources();
        removeScriptTagSDK();
    }

    annotationMNGR;
    toolbarHiderOpacity = false;
    adobeDCView = null;

    createPdfViewer(){
        this.clearViewerResources();
        document.addEventListener('adobe_dc_view_sdk.ready', () => this.onAdobeSDKReady());
    }

    async onAdobeSDKReady(){
        const { filerr } = this.props;
        const { openUrl, open_url, fileName, id } = filerr;
        this.setAdobeDCView();
        // eslint-disable-next-line no-undef
        registerViewerEvents(AdobeDC, this.adobeDCView, (event) => this.onViewerEvents(event));
        this.createAdobeViewerPromise = createAdobeViewer(this.adobeDCView, openUrl || open_url, fileName, id);
        // eslint-disable-next-line no-undef
        removeSaveButton(AdobeDC, this.adobeDCView);
        await this.createAnnotationManager();
    }

    async createAnnotationManager(){
        const { withCollaboration, context } = this.props;
        try {
            const view = await this.createAdobeViewerPromise;
            const annotationManager = await view.getAnnotationManager();
            this.annotationMNGR = new AnnotationManager(annotationManager);
            await setConfig(annotationManager, { showToolsOnTextSelection: withCollaboration });
            this.toggleHideAnnotations();
            registerAnnotationEvents(annotationManager, (event) => this.onAnnotationEvents(event));
        }
        catch (err){
            Error.send(err.message, err.code, { failureLocation: 'PDF: Adobe embed sdk: createAnnotationManager failed' });
            context.onError();
        }
    }

    setAdobeDCView(){
        const { locale } = getContext();
        // eslint-disable-next-line no-undef
        this.adobeDCView = new AdobeDC.View({
            clientId: CLIENT_ID,
            divId: ADOBE_PDF_EMBED_DIV_ID,
            locale
        });
    }

    clearViewerResources(){
        document.removeEventListener('adobe_dc_view_sdk.ready', this.onAdobeSDKReady);
        const myNode = document.getElementById(ADOBE_PDF_EMBED_DIV_ID);
        if (myNode) {
            myNode.innerHTML = '';
            this.adobeDCView = null;
        }
    }

    updatePdfAnnotations(prevProps){
        const { currentNonPendingComments, comments } = this.props;
        const AddedComment = arraySymetricDiff(currentNonPendingComments.map((comment) => comment.id), prevProps.currentNonPendingComments.map((comment) => comment.id));
        if (AddedComment.length && comments[AddedComment[0]].annotations?.length) {
            const annotationToUpdate = comments[AddedComment[0]].annotations[0];
            if (annotationToUpdate.target.selector.strokeColor === DEFAULT_ANNOTATION_COLOR) {
                this.annotationMNGR.updateAnnotation(annotationToUpdate);
            }
        }
    }

    pendingCommentsSubmitted(prevProps){
        const { currentNonPendingComments, commentsIds } = this.props;
        return currentNonPendingComments.length - 1 === prevProps.currentNonPendingComments.length &&
            commentsIds.length -1 === prevProps.commentsIds.length;
    }

    removedComments(prevProps) {
        const { pendingComments } = this.props;
        return pendingComments.length + 1 === prevProps.pendingComments.length;
    }

    changedTempAnnotations(prevTempAnnotations) {
        const { tempAnnotations } = this.props;
        return tempAnnotations.length && prevTempAnnotations.length && tempAnnotations[0].id !== prevTempAnnotations[0].id;
    }

    toggleHideAnnotations(hide = false){
        const { fadedAnnotations, annotationsHidden } = this.props;
        if (hide){
            this.annotationMNGR.deleteAnnotations([]);
        } else {
            this.annotationMNGR.addInitialAnnotations(fadedAnnotations, annotationsHidden);
        }
    }

    currentCommentChanged(currentCommentId){
        if (this.annotationMNGR) {
            if (!currentCommentId) {
                this.annotationMNGR.unselectAnnotation();
            } else {
                this.selectAnnotationByCurrentCommentId(currentCommentId);
            }
        }
    }

    selectAnnotationByCurrentCommentId(currentCommentId){
        const { comments } = this.props;
        const currentComment = comments[currentCommentId];
        if (currentComment?.annotations?.length){
            if (this.annotationMNGR) {
                this.annotationMNGR.selectAnnotation(currentComment.annotations[0].id);
            } else {
                this.goToAnnotationLocation(currentComment.annotations[0]);
            }
        }
    }

    commentsRemoved(currentCommentsIds, prevComments){
        const removedCommentIds = arraySymetricDiff(currentCommentsIds, Object.keys(prevComments));
        if (removedCommentIds.length) {
            const removedComment = prevComments[removedCommentIds[0]];
            if (removedComment.annotations?.length) {
                this.annotationMNGR.deleteAnnotations(removedComment.annotations.map((annotation) => annotation.id));
            }
        }
    }
    deleteLastTempAnnotation(tempAnnotations){
        if (tempAnnotations.length) {
            this.annotationMNGR.deleteAnnotations([tempAnnotations[tempAnnotations.length - 1].id]);
        }
    }

    removeAnnotationsWithNoComments(pdfAnnotationIds, commentsContainsAnnotation, tempAnnotations){
        const noCommentsAnnotationIds = arraySymetricDiff(pdfAnnotationIds, commentsContainsAnnotation.map((comment) => comment.annotations[0].id));
        if (noCommentsAnnotationIds.length) {
            const annotations2BDeleted = arrayDiff(noCommentsAnnotationIds, tempAnnotations.map((annotation) => annotation.id));
            if (annotations2BDeleted.length) {
                this.annotationMNGR.deleteAnnotations(annotations2BDeleted);
            }
        }
    }

    commentsContainsAnnotation(pdfAnnotationIds){
        const { comments } = this.props;
        return Object.values(comments).filter((comment) =>
            comment.annotations?.length && pdfAnnotationIds.includes(comment.annotations[0].id));
    }

    onViewerEvents(event) {
        const { setCurrentCommentId, currentCommentId } = this.props;
        if (event.type === viewerEvents.PDF_VIEWER_OPEN) {
            this.stopLoader();
            this.setState({ showToolbarHider: true });
        }
        if (event.type === viewerEvents.PREVIEW_PAGE_CLICK){
            setCurrentCommentId(undefined);
        }
        if (event.type === viewerEvents.APP_RENDERING_DONE){
            this.currentCommentChanged(currentCommentId);
        }
    }

    stopLoader() {
        const { context } = this.props;
        context.onMediaLoad({
            type: PDF_TYPE,
            element: this.iframeRef.current,
            api: {}
        });
    }

    async goToAnnotationLocation(annotation){
        const { context } = this.props;
        const PDFCoordinatePPI = 72;
        const annotationGapFromTop = 10;
        const screenHeightInch = Math.round(screen.height / getPPI());
        const page = parseInt(annotation.target.selector.node.index, 10) + 1;
        const xCoordinate = parseInt(annotation.target.selector.boundingBox[0], 10);
        const yCoordinate = (screenHeightInch * PDFCoordinatePPI) - parseInt(annotation.target.selector.boundingBox[3], 10);
        try {
            const view = await this.createAdobeViewerPromise;
            const apis = await view.getAPIs();
            setTimeout(() => apis.gotoLocation(page, xCoordinate, yCoordinate - annotationGapFromTop), 0);
        }
        catch (err){
            Error.send(err.message, err.code, { failureLocation: 'PDF: Adobe embed sdk: goToAnnotationLocation failed' });
            context.onError();
        }
    }

    onAnnotationEvents(event) {
        const { selectCommentIdByAnnotationId,
            deleteCommentByAnnotationId,
            updateCommentByAnnotation } = this.props;

        switch (event.type){
            case annotationEvents.ANNOTATION_SELECTED:
                if (!this.canEditAnnotation(event.data.id)) {
                    this.annotationMNGR.unselectAnnotation();
                }
                this.goToAnnotationLocation(event.data);
                break;
            case annotationEvents.ANNOTATION_CLICKED:
                selectCommentIdByAnnotationId(event.data.id);
                break;
            case annotationEvents.ANNOTATION_ADDED:
                this.onAnnotationAdded(event);
                break;
            case annotationEvents.ANNOTATION_DELETED:
                deleteCommentByAnnotationId(event.data.id);
                break;
            case annotationEvents.ANNOTATION_UPDATED:
                updateCommentByAnnotation(event.data);
                break;
            default:
                break;
        }
    }

    onAnnotationAdded(event) {
        const { overlayClicked, isActive, currentCommentId, fadedAnnotations } = this.props;
        const {
            isTouch
        } = this.state;
        if (!this.annotationExists(event.data.id, fadedAnnotations) && (isActive || !isTouch)) {
            collaborationEventBus.emit(OVERLAY_CLICK_EVENT);
            overlayClicked(event.data);
        } else {
            this.selectAnnotationByCurrentCommentId(currentCommentId);
        }
    }

    annotationExists(annotationId, fadedAnnotations){
        return fadedAnnotations.findIndex((annotation) => annotation.id === annotationId) >= 0;
    }

    canEditAnnotation(annotationId){
        const { pendingComments, tempAnnotations } = this.props;
        if (!pendingComments.length && !tempAnnotations.length){
            return false;
        }
        const annotations = pendingComments.
            filter((comment) => comment.annotations?.length).
            map((comment) => comment.annotations[0]).concat(tempAnnotations);
        return annotations.findIndex((annotation) => annotation.id === annotationId) >= 0;
    }

    render() {
        return (
            <>
                <div className="toolbar-overlay" style={{ opacity: this.state.showToolbarHider ? 1 : 0 }} ref={this.iframeRef}>
                </div>
                <div id={ADOBE_PDF_EMBED_DIV_ID} style={{ height: `${100}%`, width: `${100}%` }}></div>
            </>
        );
    }
}

Pdf.propTypes = {
    filerr: FilerrShape.isRequired,
    context: contextPropType,
    isActive: PropTypes.bool,
    overlayClicked: PropTypes.func.isRequired,
    fadedAnnotations: PropTypes.array,
    comments: PropTypes.object,
    setCurrentCommentId: PropTypes.func,
    currentCommentId : PropTypes.string,
    tempAnnotations: PropTypes.array,
    commentsIds: PropTypes.array,
    selectCommentIdByAnnotationId: PropTypes.func,
    deleteCommentByAnnotationId: PropTypes.func,
    updateCommentByAnnotation: PropTypes.func,
    commentBoxCancelled: PropTypes.bool,
    annotationsHidden: PropTypes.bool,
    pendingComments: PropTypes.array,
    withCollaboration: PropTypes.bool,
    currentNonPendingComments: PropTypes.array
};

const mapContextToProps = (context) => ({
    context
});

const mapStateToProps = (state) => ({
    isActive: FilesSelectors.isActiveStatus(state) && !Selectors.isSellerGroupInPreRevisionMode(state),
    fadedAnnotations: CommentsSelectors.getCurrentAnnotations(state),
    currentCommentId: CurrentsSelectors.getCurrentCommentId(state),
    tempAnnotations: CommentsSelectors.getTempAnnotations(state),
    commentsIds: CommentsSelectors.getCommentsIds(state),
    comments: CommentsSelectors.get(state),
    commentBoxCancelled: selectCommentBoxCancelled(state),
    annotationsHidden: selectAnnotationsHidden(state),
    pendingComments: CommentsSelectors.getPendingComments(state),
    withCollaboration: SettingsSelectors.isCollaborationAsked(state),
    currentNonPendingComments: CommentsSelectors.getCurrentNonPendingComments(state)
});

export default flow(
    connect(mapStateToProps, {
        overlayClicked,
        setCurrentCommentId,
        selectCommentIdByAnnotationId,
        deleteCommentByAnnotationId,
        updateCommentByAnnotation
    }),
    withContext(Context, mapContextToProps)
)(Pdf);
